import { useCallback, useEffect, useState } from "react";
import { Button, Badge, Spinner, Form } from "react-bootstrap";
import { axiosClient } from "./shared/apiConfig";
import { PageTitle } from "./Controls/PageTitle";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { toast } from "react-toastify";
import { useParamIds } from "./Data/Common";
import { connectUrl } from "./Data/ApiUrl";
import { getInitials, avatarColor } from "./Data/Avatar";
import { PersonXFill, PersonPlusFill, Link45deg } from "react-bootstrap-icons";

interface DelegateItem {
  parkerDelegateId: number;
  ownerParkerId: number;
  ownerName: string;
  delegateParkerId: number | null;
  delegateEmail: string;
  delegateName: string | null;
  inviteCode: string | null;
  isPending: boolean;
  isActive: boolean;
  createdWhen: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function DelegateAvatar({ name }: { name: string }) {
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
      style={{ width: 36, height: 36, fontSize: 12, fontWeight: 700, color: "#fff", backgroundColor: avatarColor(name) }}
    >
      {getInitials(name)}
    </span>
  );
}

export function FacilityParkerDelegateListPage() {
  const { parkerId } = useParamIds();
  const [delegates, setDelegates] = useState<DelegateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ id: number; name: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    axiosClient.get(`/monitor/parker/${parkerId}/delegate`)
      .then(res => setDelegates(res.data?.items ?? []))
      .catch(() => toast.error("Failed to load delegates"))
      .finally(() => setLoading(false));
  }, [parkerId]);

  useEffect(() => { load(); }, [load]);

  const validateEmail = (val: string) => {
    if (!val.trim()) return "Email is required";
    if (!EMAIL_RE.test(val.trim())) return "Enter a valid email address";
    return null;
  };

  const addDelegate = () => {
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }
    setEmailError(null);
    setInviting(true);
    axiosClient.post(`/monitor/parker/${parkerId}/delegate`, { email: email.trim() })
      .then(() => {
        toast.success("User invited");
        setEmail("");
        load();
      })
      .catch(err => toast.error(err?.response?.data ?? "Failed to add delegate"))
      .finally(() => setInviting(false));
  };

  const doRevoke = (id: number) => {
    setRevoking(id);
    axiosClient.post(`/monitor/parker/${parkerId}/delegate/${id}/revoke`)
      .then(() => {
        toast.success("User removed");
        load();
      })
      .catch(() => toast.error("Failed to revoke"))
      .finally(() => setRevoking(null));
  };

  return (
    <div className="p-3" style={{ maxWidth: 560 }}>
      <ConfirmModal
        show={!!confirmTarget}
        setShow={v => { if (!v) setConfirmTarget(null); }}
        title="Remove User"
        message={confirmTarget ? `Remove ${confirmTarget.name}'s access to this account?` : ""}
        confirmText="Remove"
        onConfirm={() => { if (confirmTarget) doRevoke(confirmTarget.id); }}
      />
      <PageTitle>Users</PageTitle>
      <p className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>
        Users can access this account to make payments and manage plans.
      </p>

      {/* Add form */}
      <div className="card border-0 mb-3" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
        <div className="card-body p-3">
          <p className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>Invite user by email</p>
          <div className="d-flex gap-2 flex-column flex-sm-row">
            <div className="flex-grow-1">
              <Form.Control
                type="email"
                placeholder="name@example.com"
                value={email}
                isInvalid={!!emailError}
                onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(null); }}
                onKeyDown={e => e.key === "Enter" && addDelegate()}
                size="sm"
              />
              {emailError && (
                <div className="text-danger mt-1" style={{ fontSize: "0.78rem" }}>{emailError}</div>
              )}
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={addDelegate}
              disabled={inviting}
              className="d-flex align-items-center gap-1 flex-shrink-0"
            >
              {inviting ? <Spinner size="sm" /> : <PersonPlusFill size={13} />}
              {inviting ? "Inviting…" : "Invite User"}
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-4"><Spinner /></div>
      ) : delegates.length === 0 ? (
        <div className="card border-0" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
          <div className="card-body text-center text-muted py-4" style={{ fontSize: "0.875rem" }}>
            No users for this parker.
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {delegates.map(d => {
            const displayName = d.delegateName ?? d.delegateEmail;
            return (
              <div key={d.parkerDelegateId} className="card border-0" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                <div className="card-body p-2 d-flex flex-wrap align-items-center gap-2">
                  <DelegateAvatar name={displayName} />
                  <div className="flex-grow-1 min-w-0" style={{ flexBasis: 0, minWidth: 100 }}>
                    <div className="fw-semibold text-truncate" style={{ fontSize: "0.875rem" }}>
                      {d.delegateName ?? <span className="text-muted fst-italic">Pending signup</span>}
                    </div>
                    <div className="text-muted text-truncate" style={{ fontSize: "0.78rem" }}>{d.delegateEmail}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    {d.isPending
                      ? <Badge bg="warning" text="dark" pill style={{ fontSize: "0.7rem" }}>Pending</Badge>
                      : <Badge bg="success" pill style={{ fontSize: "0.7rem" }}>Active</Badge>}
                    {d.isPending && d.inviteCode && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="d-flex align-items-center gap-1 border-0 py-1"
                        title="Copy invite link"
                        style={{ fontSize: "0.78rem" }}
                        onClick={() => {
                          const url = connectUrl(`/signup?delegate-invite=${d.inviteCode}`);
                          navigator.clipboard.writeText(url).then(() => toast.success("Invite link copied"));
                        }}
                      >
                        <Link45deg size={12} /> Copy Link
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="d-flex align-items-center gap-1 border-0 py-1"
                      onClick={() => setConfirmTarget({ id: d.parkerDelegateId, name: d.delegateName ?? d.delegateEmail })}
                      disabled={revoking === d.parkerDelegateId}
                      title="Remove user"
                      style={{ fontSize: "0.78rem" }}
                    >
                      {revoking === d.parkerDelegateId
                        ? <Spinner size="sm" />
                        : <PersonXFill size={12} />}
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
